import * as React from "react";
import { useDispatch } from "react-redux";

import { makeStyles } from "@material-ui/styles";

// import UploadIcon from "mdi-material-ui/Upload";
import DownloadIcon from "mdi-material-ui/Download";
// import PrintIcon from "@material-ui/icons/Print";
import DeleteIcon from "@material-ui/icons/Delete";
import ViewIcon from "@material-ui/icons/RemoveRedEye";

import { PageHeader, TextInput, required, translate, showNotification } from "jazasoft";

import MuiTable from "../../../components/MuiTable";
import MyFileInput from "../../../components/MyFileInput";
import FormDialog from "../../../components/FormDialog";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useTenant, useTime, useUnit } from "../../../hooks";
import { findMinValue, isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { formatDateTime } from "../../../utils/dateUtils";
import FabricReleaseDrawer from "./FabricReleaseDrawer";
import SettingKeys from "../../../utils/SettingKeys";
import { Entity, QcStatus, RollType } from "../../../utils/types";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { getGroupName } from "../../grouping/groupingHelper";
import { colorLightGreen, colorOrange, colorSecondary, colorTomatoRed } from "../../../utils/colors";
import AuditDetailsDrawer from "./AuditDetailsDrawer";
import useWarehouse from "../../../hooks/useWarehouse";
import { convertTableToCell } from "../../../utils/excelHelper";
import { getEffFlowInfoList } from "../../../helpers/orderHelper";

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
            ? colorTomatoRed.dark
            : value === QcStatus.ON_HOLD
              ? colorOrange.dark
              : value === QcStatus.PENDING
                ? colorSecondary.dark
                : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const StatusField = ({ value, record, onClick }) => {
  return (
    <span
      onClick={() => onClick && onClick(record)}
      style={{
        backgroundColor: ["Synced"].includes(value)
          ? colorLightGreen.dark
          : ["Sync Error"].includes(value)
            ? colorTomatoRed.dark
            : colorSecondary.dark,
        color: "white",
        padding: "4px 8px",
        borderRadius: 4,
        cursor: "pointer"
      }}>
      {value}
    </span>
  );
};

const selectActions = [
  {
    name: "release",
    tooltip: "Release Fabric",
    showLabel: true,
    options: { variant: "contained", color: "primary", style: { marginLeft: "1em" } }
  }
];

const footerActions = [
  {
    name: "download",
    tooltip: "Download",
    icon: <DownloadIcon />,
    options: { variant: "outlined", color: "primary" }
  }
  // {
  //   name: "releaseByExcel",
  //   tooltip: "Release by Excel",
  //   icon: <UploadIcon />,
  //   options: { variant: "outlined", color: "primary" }
  // }
];

const releaseActions = [
  {
    name: "view",
    tooltip: "View",
    icon: <ViewIcon />,
    options: { variant: "outlined", color: "primary" }
  },
  {
    name: "delete",
    tooltip: "Delete",
    icon: <DeleteIcon />,
    options: { variant: "outlined", style: { color: "red" } }
  }
  // {
  //   name: "print",
  //   tooltip: "Print",
  //   icon: <PrintIcon />,
  //   options: { variant: "outlined", color: "primary" }
  // }
];

const rollColumns = [
  { dataKey: "rollNo", title: "Factory Roll No" },
  { dataKey: "supplierRollNo", title: "Supplier Roll No" },
  { dataKey: "qcStatus", title: "QC Status", filterOptions: { filter: true }, render: renderQcStatus },
  { dataKey: "invStatus", title: "Inventory Status", filterOptions: { filter: true } },
  { dataKey: "rollType", title: "Roll Type", filterOptions: { filter: true } },
  { dataKey: "weight", title: "Weight", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 3)} kg`) },
  { dataKey: "length", title: "Length", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 1)} m`) },
  { dataKey: "width", title: "Cutable Width", render: value => (isEmpty(value) ? "-" : `${toFixedValue(value, 1)} cm`) },
  { dataKey: "shade", title: "Shade", filterOptions: { filter: true, multiSelect: true } },
  { dataKey: "groupName", title: "Group", filterOptions: { filter: true, multiSelect: true }, length: 50 },
  {
    dataKey: "layNo",
    title: "Lay No",
    render: value => (isEmpty(value) || value === Number.MAX_VALUE ? "-" : `Lay ${value}`),
    filterOptions: { filter: true, multiSelect: true }
  },
  { dataKey: "warehouse", filterOptions: { filter: true }, title: "Current \nWarehouse", render: (value, shortValue) => shortValue || "-" }
];

const sortRoll = (a, b) => {
  if (a.layNo !== b.layNo) {
    return a.layNo - b.layNo;
  } else if (!isEmpty(a.group?.name) && !isEmpty(b.group?.name)) {
    const aGroupNo = parseInt(a.group.name.replaceAll("G", ""));
    const bGroupNo = parseInt(b.group.name.replaceAll("G", ""));
    return aGroupNo - bGroupNo;
  } else {
    return a.id - b.id;
  }
};

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  }
});

const FabricReleaseView = props => {
  const { id, translate, roles, hasAccess } = props;
  const classes = useStyles();

  const [version, setVersion] = useSafeState(0);
  const [selectedKey, setSelectedKey] = useSafeState();
  const [dialogState, setDialogState] = useSafeState({ active: false });
  const [drawerState, setDrawerState] = useSafeState({ open: false, name: null, fabricRelease: null });
  const [filterValues, setFilterValues] = useSafeState({});

  const [mutate] = useMutation();
  const dispatch = useDispatch();
  const tenant = useTenant();
  const [download] = useMutation({ rawResponse: true });
  const { utc_offset } = useTime();
  const { allUnitList } = useUnit();
  const { allWhList } = useWarehouse();

  const orderFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.ORDERS);
  const fabricFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FABRICS);
  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, Entity.FLOW_INFO);

  const { data: cutOrder = {} } = useQuery({ url: `order-flow/${id}` });
  const { data: order = {} } = useQuery(!isEmpty(cutOrder.orderId) && { url: `orders/${cutOrder.orderId}` });
  const { data: fabricReleaseList = [] } = useQuery({ url: `fabric-release`, params: { search: `orderFlow.id==${id}` }, version });
  const { data: rollInfoList = [] } = useQuery({ url: `rolls`, params: { search: `cutOrderFlow.id==${id};parentId=='NULL'`, size: 10000 }, version });
  const { data: ledgerList = [] } = useQuery(
    !isEmpty(cutOrder) && { url: `ledgers`, params: { search: `order.id==${cutOrder.orderId}`, size: 1000 } }
  );
  const { data: layList = [] } = useQuery(!isEmpty(cutOrder) && { url: `lays`, params: { search: `orderFlow.id==${cutOrder.id}` } });
  const { data: externalAppList = [] } = useQuery({ url: `externalApps`, params: { size: 1000 } });

  const fabReleaseOpList = externalAppList
    ?.flatMap(externalApp => externalApp?.integrationOperationList?.map(op => ({ ...op, externalApp })) || [])
    .filter(op => op.entityId === "FabricRelease");

  const infoColumns = useDeepMemo(() => {
    const fields = [...orderFields, ...fabricFields, ...flowInfoFields, "fpo", "unitId", "part", "lot", "fabColor", "allocatedQty", "releasedQty"];

    let cols = [
      { dataKey: "poRef", title: "PO Ref." },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "customerPoRef", title: "Customer PO Ref.", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", highlight: true },
      { dataKey: "customer", title: "Customer " },
      { dataKey: "productName", title: "Product ", highlight: true },
      { dataKey: "style", title: "Style", highlight: true },
      { dataKey: "color", title: "Style Color", highlight: true },
      { dataKey: "inseam", title: "Inseam", highlight: true },
      { dataKey: "destination", title: "Destination", highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", highlight: true },
      { dataKey: "part", title: "Part", highlight: true },
      { dataKey: "lot", title: "Lot", highlight: true },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "itemCode", title: "Item Code" },
      { dataKey: "fabColor", title: "Fabric Color" },
      { dataKey: "itemDesc", title: "Item Description" },
      { dataKey: "articleName", title: "Article Name" },
      { dataKey: "allocatedQty", title: "Allocated Qty" },
      { dataKey: "releasedQty", title: "Released Qty" }
    ];
    return cols.filter(c => fields.includes(c.dataKey));
  }, [orderFields, fabricFields, flowInfoFields, fabricReleaseList, allUnitList]);

  const infoRows = useDeepMemo(() => {
    if (isEmpty(order) || isEmpty(cutOrder) || isEmpty(ledgerList)) return [];
    const flowInfoList = getEffFlowInfoList(order, cutOrder);

    return flowInfoList.map(flowInfo => {
      const ledger = ledgerList?.find(
        e => e.productId === flowInfo.product?.id && e.style === flowInfo.style && e.styleColor === flowInfo.color && e.part === cutOrder.part
      );
      const allocatedLength = rollInfoList.filter(ri => ri.styleColor === flowInfo.styleColor).reduce((acc, ri) => acc + ri.length, 0);
      const allocatedWeight = rollInfoList.filter(ri => ri.styleColor === flowInfo.styleColor).reduce((acc, ri) => acc + (ri.weight || 0), 0);

      const releasedLength = fabricReleaseList
        .filter(fr => fr.style === flowInfo.style && fr.styleColor === flowInfo.color)
        .reduce((acc, e) => acc + e.releaseLength, 0);
      const releasedWeight = fabricReleaseList
        .filter(fr => fr.style === flowInfo.style && fr.styleColor === flowInfo.color)
        .reduce((acc, e) => acc + (e.releaseWeight || 0), 0);

      return {
        ...cutOrder,
        ...flowInfo,
        ...ledger?.fabric,
        flowInfo,
        key: `${cutOrder.id}-${flowInfo.id}`,
        color: flowInfo.color,
        fabColor: ledger?.fabColor,
        part: cutOrder?.part,
        productName: flowInfo?.product?.name,
        lot: cutOrder?.lotType === "Bulk" ? `Lot ${cutOrder.lot}` : cutOrder?.lotType,
        allocatedQty: `${toFixedValue(allocatedLength)} m${allocatedWeight > 0 ? ` (${toFixedValue(allocatedWeight)} kg)` : ""}`,
        releasedQty: `${toFixedValue(releasedLength)} m${releasedWeight > 0 ? ` (${toFixedValue(releasedWeight)} kg)` : ""}`,
        releasedLength
      };
    });
  }, [order, cutOrder, ledgerList, rollInfoList, fabricReleaseList]);

  useDeepEffect(() => {
    if (isEmpty(selectedKey) && !isEmpty(infoRows)) {
      setSelectedKey(infoRows[0]?.key);
    }
  }, [infoRows]);

  const fabReleaseColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "txn", title: "Transaction Number" },
      { dataKey: "challanNo", title: "Challan No" },
      { dataKey: "releasedQty", title: "Released Qty" },
      { dataKey: "releasedDate", title: "Released Date" },
      { dataKey: "releasedBy", title: "Released By" },
      { dataKey: "status", title: "Inventory Status" },
      {
        dataKey: "syncStatus",
        title: "Sync Status",
        render: (value, _, row) =>
          value && <StatusField value={value} record={row} onClick={() => setDrawerState({ open: true, name: "audit", fabricRelease: row })} />
      }
    ];
    if (isEmpty(fabReleaseOpList)) {
      cols = cols.filter(c => c.dataKey !== "syncStatus");
    }
    return cols;
  }, [fabReleaseOpList]);

  const fabReleaseRows = useDeepMemo(() => {
    const selectedRow = infoRows.find(e => e.key === selectedKey);
    let rows = fabricReleaseList
      .filter(fr => fr.style === selectedRow?.style && fr.styleColor === selectedRow?.color)
      .sort((a, b) => b.id - a.id)
      .map(e => ({ ...e, externalId: !isEmpty(e.externalIds) ? JSON.parse(e.externalIds)[0]?.externalId : null }))
      .map(fabricRelease => {
        const appIds = fabReleaseOpList.map(e => e.externalApp?.appId);
        const externalIds = !isEmpty(fabricRelease.externalIds) ? JSON.parse(fabricRelease.externalIds) : [];
        const syncedVersion = findMinValue(appIds.map(appId => externalIds.find(e => e.appId === appId)?.syncedVersion || 0));
        const syncError = appIds
          .map(appId => externalIds.find(e => e.appId === appId)?.errorMessage || "")
          .filter(e => !isEmpty(e))
          .join(", ");
        const syncStatus = syncedVersion > 0 && isEmpty(syncError) ? "Synced" : !isEmpty(syncError) ? "Sync Error" : "Not Synced";

        return {
          ...fabricRelease,
          releasedDate: !isEmpty(utc_offset) ? formatDateTime(fabricRelease.releasedAt, utc_offset) : "-",
          releasedQty: `${toFixedValue(fabricRelease.releaseLength)} m${
            !isEmpty(fabricRelease.releaseWeight) && fabricRelease.releaseWeight > 0 ? ` (${toFixedValue(fabricRelease.releaseWeight)} kg)` : ""
          }`,
          syncStatus,
          status: fabricRelease.rollInfoList?.every(r => r.invStatus === "Issued")
            ? "Issued"
            : fabricRelease.rollInfoList?.some(r => r.invStatus === "Issued")
              ? "Issued Partially"
              : !isEmpty(fabricRelease.externalId)
                ? "Requested"
                : "-"
        };
      });
    if (rows.length > 1) {
      const totalLength = rows.reduce((acc, e) => acc + e.releaseLength, 0);
      const totalWeight = rows.reduce((acc, e) => acc + (e.releaseWeight || 0), 0);
      rows = [
        ...rows,
        {
          txn: "Total",
          releasedQty: `${toFixedValue(totalLength)} m${totalWeight > 0 ? ` (${toFixedValue(totalWeight)} kg)` : ""}`,
          totalRow: true
        }
      ];
    }
    return rows;
  }, [fabricReleaseList, selectedKey, utc_offset, fabReleaseOpList]);

  const availableList = useDeepMemo(() => {
    if (isEmpty(rollInfoList)) return [];
    const selectedRow = infoRows.find(e => e.key === selectedKey);
    const flowInfo = selectedRow?.flowInfo || {};

    const rollList = rollInfoList.filter(ri => isEmpty(ri.fabricReleaseId) && ri.styleColor === flowInfo.styleColor);
    const layRollList = [];
    const addedRollIds = new Set();
    layList
      .sort((a, b) => a.layNo - b.layNo)
      .forEach(lay => {
        lay.rowList.forEach(row => {
          if (!addedRollIds.has(row.rollInfo.id)) {
            layRollList.push({ ...row.rollInfo, lay });
            addedRollIds.add(row.rollInfo.id);
          }
        });
      });

    const finalRollList = rollList
      .map(roll => {
        const lay = layRollList.find(e => e.id === roll.id)?.lay;
        const group = cutOrder?.groupList?.find(e => e.id === roll.finalGroupId);
        return {
          ...roll,
          group,
          lay,
          groupName: getGroupName(group),
          layNo: lay?.layNo,
          warehouse: allWhList.find(w => w.code === roll.currWarehouse)?.name || roll.currWarehouse
        };
      })
      .sort(sortRoll);

    return finalRollList;
  }, [rollInfoList, layList, cutOrder, allWhList, selectedKey]);

  const finalRollColumns = useDeepMemo(() => {
    if (availableList.every(e => isEmpty(e.weight) || e.weight === 0.0)) {
      return rollColumns.filter(c => c.dataKey !== "weight");
    }
    return rollColumns;
  }, [availableList, rollColumns]);

  const onDialogSubmit = values => {
    const selectedRow = infoRows.find(e => e.key === selectedKey);
    const flowInfo = selectedRow?.flowInfo || {};
    const ledger = ledgerList?.find(
      e => e.productId === flowInfo.product?.id && e.style === flowInfo.style && e.styleColor === flowInfo.color && e.part === cutOrder.part
    );
    const rollList = dialogState?.rollInfoList || [];
    if (rollList.some(r => r.qcStatus === QcStatus.PENDING || r.qcStatus === QcStatus.ON_HOLD)) {
      dispatch(showNotification("Fabric release failed. QC of some rolls is Pending", { type: "danger" }));
      return;
    }

    const releaseLength = rollList.reduce((acc, ri) => acc + ri.length, 0);
    const releaseWeight = rollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);

    const data = {
      ...values,
      orderFlowId: id,
      style: flowInfo?.style,
      styleColor: flowInfo?.color,
      fabricId: ledger?.fabric?.id,
      fabColor: ledger?.fabColor,
      releaseLength,
      releaseWeight: releaseWeight > 0 ? releaseWeight : null,
      rollInfoList: rollList.map(({ id, rollNo, supplierRollNo }) => ({ id, rollNo, supplierRollNo }))
    };

    const options = {
      url: "fabric-release",
      method: "post",
      data
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Fabric Released successfully"));
        setVersion(v => v + 1);
        setDialogState({});
      }
    });
  };

  const onSelectActionClick = (event, action, selectedRows, onActionComplete) => {
    if (action === "release") {
      setDialogState({ active: true, name: action, rollInfoList: selectedRows });
    }
    onActionComplete && onActionComplete(selectedRows);
  };

  const onFooterActionClick = (event, action, rows, filterValues, onActionComplete) => {
    if (action === "release") {
      setDialogState({ active: true, name: action });
    } else if (action === "download") {
      const filename = `ROLLS`.replaceAll(" ", "-").replaceAll("/", "-");
      const infoRow = infoRows.find(e => e.key === selectedKey);

      const orderInfoColumns = [
        { dataKey: "erpNo", title: "ERP No" },
        { dataKey: "fpo", title: "FPO" },
        { dataKey: "unit", title: "Unit" },
        { dataKey: "bpo", title: "BPO" },
        { dataKey: "customer", title: "Customer" },
        { dataKey: "product", title: "Product" },
        { dataKey: "style", title: "Style" },
        { dataKey: "color", title: "Color" },
        { dataKey: "inseam", title: "Inseam" },
        { dataKey: "destination", title: "Destination" },
        { dataKey: "deliveryDate", title: "Delivery Date" },
        { dataKey: "part", title: "Part" },
        { dataKey: "lot", title: "Lot" },
        { dataKey: "orderQty", title: "Order Qty" }
      ];
      const orderInfoRows = [
        {
          erpNo: infoRow?.poRef,
          fpo: infoRow?.fpo,
          unit: allUnitList?.find(e => e.unitId === infoRow?.unitId)?.name || "",
          bpo: infoRow?.customerFlowRefs,
          customer: infoRow?.customer,
          product: infoRow?.product?.name,
          style: infoRow?.styles,
          color: infoRow?.color,
          inseam: infoRow?.inseam,
          destination: infoRow?.destination,
          deliveryDate: infoRow?.deliveryDate,
          part: infoRow?.part,
          lot: infoRow?.lot,
          orderQty: infoRow?.orderQty
        }
      ];

      const fabricInfoColumns = [
        { dataKey: "itemCode", title: "Item Code" },
        { dataKey: "fabricColor", title: "Fabric Color" },
        { dataKey: "itemDescription", title: "Item Description" },
        { dataKey: "articleName", title: "Article Name" },
        { dataKey: "allocatedQty", title: "Allocated Qty" },
        { dataKey: "extraFabric", title: "Extra Fabric" },
        { dataKey: "releasedQty", title: "Released Qty" },
        { dataKey: "balanceQty", title: "Balance Qty" }
      ];

      const fabricInfoRows = [
        {
          itemCode: infoRow?.itemCode,
          fabricColor: infoRow?.fabColor,
          itemDescription: infoRow?.itemDesc,
          articleName: infoRow?.articleName,
          allocatedQty: infoRow?.allocatedQty,
          extraFabric: rollInfoList?.filter(e => e.rollType === "EXCESS").reduce((acc, rol) => acc + rol?.length, 0) || 0,
          releasedQty: infoRow?.releasedQty,
          balanceQty: rollInfoList.reduce((acc, rol) => acc + rol?.length, 0) - infoRow?.releasedLength || 0
        }
      ];

      const columnList = finalRollColumns;

      const rows = availableList.filter(row =>
        Object.keys(filterValues)
          .filter(key => !isEmpty(filterValues[key]))
          .every(key => (["layNo", "shade", "groupName"].includes(key) ? filterValues[key].includes(row[key]) : filterValues[key] === row[key]))
      );
      const extraRows = rows.filter(row => row.rollType === RollType.EXTRA);
      const normalRows = rows.filter(row => row.rollType !== RollType.EXTRA);

      const extraTotalRow = {
        rollNo: "Total",
        length: extraRows.reduce((acc, e) => acc + e.length, 0),
        weight: extraRows.reduce((acc, e) => acc + (e.weight || 0), 0)
      };
      const normalTotalRow = {
        rollNo: "Total",
        length: normalRows.reduce((acc, e) => acc + e.length, 0),
        weight: normalRows.reduce((acc, e) => acc + (e.weight || 0), 0)
      };

      const maxColNo = orderInfoColumns.length;
      const baiscInfoCells = convertTableToCell(orderInfoColumns, orderInfoRows, 4, maxColNo);
      const fabricInfoCells = convertTableToCell(fabricInfoColumns, fabricInfoRows, 6 + orderInfoRows.length, maxColNo);
      const plannedRollCells = convertTableToCell(
        columnList,
        [...normalRows, normalTotalRow],
        8 + orderInfoRows.length + fabricInfoRows.length,
        maxColNo
      );
      const extraRollCells = isEmpty(extraRows)
        ? []
        : convertTableToCell(
            columnList,
            [...extraRows, extraTotalRow],
            11 + orderInfoRows.length + fabricInfoRows.length + normalRows.length,
            maxColNo
          );

      const unit = allUnitList.find(e => e.unitId === infoRow?.unitId) || {};
      let cellList = [
        {
          rowNo: 1,
          colNo: 1,
          value: unit?.displayName || unit?.name || tenant?.name || "",
          colEnd: maxColNo,
          cellStyle: { hAlign: "center", border: false, bold: true, fontHeight: 24 },
          cellHeight: 32
        },
        {
          rowNo: 2,
          colNo: 1,
          value: "Fabric Pending for Release",
          colEnd: maxColNo,
          cellStyle: { hAlign: "center", border: false, fontHeight: 18 },
          cellHeight: 32
        },
        {
          rowNo: 3,
          colNo: 1,
          value: "Order Info",
          colEnd: maxColNo,
          cellStyle: { fontHeight: 15, bold: true }
        },
        ...baiscInfoCells,
        {
          rowNo: 5 + orderInfoRows.length,
          colNo: 1,
          value: "Fabric Info",
          colEnd: maxColNo,
          cellStyle: { fontHeight: 15, bold: true }
        },
        ...fabricInfoCells,
        {
          rowNo: 7 + orderInfoRows.length + fabricInfoRows.length,
          colNo: 1,
          value: "Planned Rolls",
          colEnd: maxColNo,
          cellStyle: { fontHeight: 15, bold: true }
        },
        ...plannedRollCells
      ];
      if (!isEmpty(extraRollCells)) {
        cellList = [
          ...cellList,
          {
            rowNo: 10 + orderInfoRows.length + fabricInfoRows.length + normalRows.length,
            colNo: 1,
            value: "Extra Rolls",
            colEnd: maxColNo,
            cellStyle: { fontHeight: 15, bold: true }
          },
          ...extraRollCells
        ];
      }

      const options = {
        url: `export/excel`,
        method: "post",
        responseType: "arraybuffer",
        params: { filename },
        data: [{ sheetNo: 0, sheetName: "Fabric Pending for Release", autoSizeColumn: false, excelData: { cellList } }]
      };

      download(options, {
        onSuccess: response => {
          if (response.status === 200) {
            const header = response.headers["content-disposition"];
            var fileName = header ? header.match(/filename="(.+)"/)[1] : `${filename}.xlsx`;

            let blob = new Blob([response.data], {
              type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            let link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
          }
          onActionComplete && onActionComplete(rows);
        }
      });
    }
    onActionComplete && onActionComplete(rows);
  };

  const onInlineActionClick = (event, action, row) => {
    if (action === "view") {
      setDrawerState({ open: true, name: "details", fabricRelease: row });
    } else if (action === "delete") {
      const options = {
        url: `fabric-release/${row.id}`,
        method: "delete"
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Fabric Release deleted successfully"));
          setVersion(v => v + 1);
        }
      });
    } else if (action === "print") {
      dispatch(showNotification("Print feature is not available yet."));
    }
  };

  const onDrawerClose = () => {
    setVersion(v => v + 1);
    setDrawerState({ open: false });
  };

  const finalReleaseActions = hasPrivilege(roles, hasAccess, ResourceId.FABRIC_RELEASE, Scope.DELETE)
    ? releaseActions
    : releaseActions.filter(a => a.name !== "delete");

  const finalFooterActions = hasPrivilege(roles, hasAccess, ResourceId.FABRIC_RELEASE, Scope.WRITE)
    ? footerActions
    : footerActions.filter(a => a.name !== "releaseByExcel");

  const fileteredRows = availableList
    .filter(row => isEmpty(filterValues?.shade) || filterValues?.shade.includes(row.shade))
    .filter(row => isEmpty(filterValues?.group) || filterValues?.group.includes(row.group))
    .filter(row => isEmpty(filterValues?.layNo) || filterValues?.layNo.includes(row.layNo));
  const availableLength = fileteredRows.reduce((acc, e) => acc + e.length, 0);
  const availableWeight = fileteredRows.reduce((acc, e) => acc + (e.weight || 0), 0);

  const title = `Available for Release: ${Number(toFixed(availableLength, 1))} m${
    availableWeight > 0 ? ` (${toFixedValue(availableWeight)} kg)` : ""
  }`;

  return (
    <div>
      <PageHeader title="Fabric Release" />

      <FabricReleaseDrawer
        open={drawerState.open && drawerState.name === "details"}
        orderFlow={cutOrder}
        layList={layList}
        fabricRelease={drawerState.fabricRelease}
        roles={roles}
        hasAccess={hasAccess}
        onDrawerClose={onDrawerClose}
        infoRow={infoRows.find(e => e.key === selectedKey)}
      />

      <AuditDetailsDrawer
        open={drawerState.open && drawerState.name === "audit"}
        fabricRelease={drawerState.fabricRelease}
        roles={roles}
        hasAccess={hasAccess}
        onDrawerClose={onDrawerClose}
      />

      <FormDialog
        maxWidth="sm"
        title="Release Fabric"
        open={dialogState.active || false}
        onSubmit={onDialogSubmit}
        onClose={() => setDialogState({})}>
        <TextInput label="resources.fab-release.history.challanNo" source="challanNo" xs={12} fullWidth />
        {dialogState?.name === "releaseByExcel" && (
          <MyFileInput
            source="file"
            label={"Excel File"}
            accept={{
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
              "application/vnd.ms-excel": [".xls"]
            }}
            message={translate("js.message.drop_file", { _: "Drag and drop file here, or click to select file" })}
            xs={12}
            fullWidth
            validate={required()}
          />
        )}
      </FormDialog>

      <div className={classes.content}>
        {/* PO Details */}
        <div>
          <MuiTable
            title="PO Details"
            toolbar
            columns={infoColumns}
            rows={infoRows}
            rowStyle={({ row }) => (selectedKey === row.key ? { backgroundColor: "#BFDDF8" } : {})}
            idKey="key"
            parentIdKey="parentKey"
            i18nKey="resources.fab-release.fields"
            onRowClick={row => setSelectedKey(row.key)}
          />
        </div>

        {/* Fabric Release History */}
        <div className={classes.mrTop}>
          <MuiTable
            title="Fabric Release History"
            toolbar
            columns={fabReleaseColumns}
            rows={fabReleaseRows}
            inlineActions={finalReleaseActions}
            onInlineActionClick={onInlineActionClick}
            i18nKey="resources.fab-release.history"
          />
        </div>

        <div className={classes.mrTop}>
          <MuiTable
            title={title}
            toolbar
            pageable
            searchable
            searchKeys={["rollNo", "supplierRollNo"]}
            selectable={hasPrivilege(roles, hasAccess, ResourceId.FABRIC_RELEASE, Scope.WRITE)}
            pageSelect={false}
            columns={finalRollColumns}
            rows={availableList}
            selectActions={selectActions}
            footerActions={finalFooterActions}
            onFilter={setFilterValues}
            onSelectActionClick={onSelectActionClick}
            onFooterActionClick={onFooterActionClick}
          />
        </div>
      </div>
    </div>
  );
};

export default translate(FabricReleaseView);
